import { useState } from "react";
import { Link } from "react-router-dom";
import { accessNote } from "@/components/settings/ExtensionConsentRow";
import { FormModal, useToast } from "@/components/shared";
import { getApiErrorMessage } from "@/lib";
import { integrationsService } from "@/lib/services";
import type { ExtensionAccessRequest } from "@/types";

/**
 * Asks the user to turn on an extension they turned off when the assistant
 * needed it for their last message. Saves the same choice as the switch in
 * Settings → Extensions; the chat then retries the message.
 */
export function ExtensionAccessModal({
  request,
  onClose,
  onTurnedOn,
}: Readonly<{ request: ExtensionAccessRequest; onClose: () => void; onTurnedOn: () => void }>) {
  const { showToast } = useToast();
  const [busy, setBusy] = useState(false);
  const access = `${request.name} ${request.permission} access`;

  const turnOn = async () => {
    try {
      setBusy(true);
      await integrationsService.setExtensionPreference(request.slug, request.permission, true);
      onTurnedOn();
    } catch (err) {
      showToast(getApiErrorMessage(err, `Failed to allow ${access}`), "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <FormModal
      title={`Allow ${access}?`}
      loading={busy}
      maxWidth="max-w-lg"
      onClose={() => !busy && onClose()}
      onSubmit={(e) => {
        e.preventDefault();
        void turnOn();
      }}
      footer={
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-content-secondary hover:bg-interactive-hover rounded-lg"
          >
            Not now
          </button>
          <button
            type="submit"
            className="px-4 py-2 text-sm font-medium text-white bg-brand-primary hover:bg-brand-primary/90 rounded-lg"
          >
            Allow and retry
          </button>
        </div>
      }
    >
      <div className="space-y-3 text-sm text-content-secondary">
        <p>{access} isn't allowed in your settings, so AnthmAI couldn't use it for your last message.</p>
        <p>
          {accessNote(request.slug, request.permission)} Allow it to retry your message, or manage it anytime in{" "}
          <Link to="/settings?tab=extensions" className="text-brand-primary hover:underline">
            Settings → Extensions
          </Link>
          .
        </p>
      </div>
    </FormModal>
  );
}
